class HimalayanProductPurchase extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.querySelector('[data-variant-select]')?.addEventListener('change', () => this.syncVariant(), options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-demo-button]')) this.querySelector('[data-demo-message]').hidden = false;
    }, options);
    this.addEventListener('submit', (event) => {
      if (this.dataset.demo !== 'true') return;
      event.preventDefault();
      this.querySelector('[data-demo-message]').hidden = false;
    }, options);
    queueMicrotask(() => {
      if (this.isConnected && !options.signal.aborted) this.syncVariant();
    });
  }

  syncVariant() {
    const option = this.querySelector('[data-variant-select]')?.selectedOptions[0];
    if (!option) return;
    const price = this.querySelector('[data-pdp-price]');
    if (price) price.textContent = option.dataset.price;
    const addButton = this.querySelector('[data-add-button]');
    if (addButton) {
      addButton.disabled = this.dataset.demo === 'true' || this.dataset.fixedPrice !== 'true' || option.dataset.positive !== 'true' || option.dataset.available !== 'true';
      addButton.textContent = option.dataset.available === 'true' ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }
    this.dispatchEvent(new CustomEvent('himalayan:variant-change', {
      bubbles: true,
      detail: { variantId: option.value, mediaId: option.dataset.mediaId || '' },
    }));
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

class HimalayanProductGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.matches('[data-gallery-index]')) this.selectIndex(target.dataset.galleryIndex);
      if (target.matches('[data-gallery-close]')) target.closest('dialog').close();
      if (target.matches('[data-zoom]')) {
        const source = target.closest('[data-gallery-panel]').querySelector('img');
        const zoom = this.querySelector('[data-zoom-image]');
        zoom.src = source.src;
        zoom.alt = source.alt;
        this.querySelector('[data-zoom-dialog]').showModal();
      }
    }, { signal: this.controller.signal });
  }

  selectMedia(mediaId) {
    if (!mediaId) return;
    const panel = [...this.querySelectorAll('[data-gallery-panel]')].find((item) => item.dataset.mediaId === String(mediaId));
    if (panel) this.selectIndex(panel.dataset.galleryPanel);
  }

  selectIndex(index) {
    const panels = [...this.querySelectorAll('[data-gallery-panel]')];
    if (!panels.some((panel) => panel.dataset.galleryPanel === index)) return;
    this.querySelectorAll('[data-gallery-index]').forEach((button) => button.setAttribute('aria-current', String(button.dataset.galleryIndex === index)));
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.galleryPanel !== index;
      if (panel.hidden) panel.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.querySelectorAll('video').forEach((video) => video.pause());
    this.querySelector('[data-zoom-dialog]')?.close();
  }
}

class HimalayanProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.matches('[role="tab"]')) this.selectTab(target);
      if (target.matches('[data-enquiry-open]')) this.querySelector('[data-enquiry-dialog]').showModal();
      if (target.matches('[data-dialog-close]')) target.closest('dialog').close();
      if (target.matches('[data-demo-enquiry-send]') && target.closest('form').reportValidity()) {
        this.querySelector('[data-enquiry-demo-message]').hidden = false;
      }
    }, options);
    this.addEventListener('keydown', (event) => {
      if (!event.target.matches('[role="tab"]')) return;
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      const current = tabs.indexOf(event.target);
      let next;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (current + tabs.length - 1) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      this.selectTab(tabs[next]);
      tabs[next].focus();
    }, options);
    this.addEventListener('himalayan:variant-change', (event) => {
      if (event.target.closest('himalayan-product') !== this) return;
      this.querySelector('himalayan-product-gallery')?.selectMedia(event.detail.mediaId);
      this.querySelectorAll('[data-product-facts]').forEach((table) => {
        const source = [...table.querySelectorAll('[data-variant-facts-template]')].find((item) => item.dataset.variantFactsTemplate === event.detail.variantId);
        const target = table.querySelector('[data-variant-facts]');
        if (!source || !target) return;
        target.replaceChildren(source.content.cloneNode(true));
        const empty = table.querySelector('[data-variant-facts-empty]');
        if (empty) empty.hidden = Boolean(target.querySelector('tr'));
      });
    }, options);
    if (this.dataset.demo === 'true') {
      this.querySelector('[data-enquiry-dialog] form')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.querySelector('[data-enquiry-demo-message]').hidden = false;
      }, options);
    }
  }

  selectTab(selected) {
    this.querySelectorAll('[role="tab"]').forEach((tab) => {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`).hidden = !active;
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.querySelector('[data-enquiry-dialog]')?.close();
  }
}

if (!customElements.get('himalayan-product-gallery')) customElements.define('himalayan-product-gallery', HimalayanProductGallery);
if (!customElements.get('himalayan-product-purchase')) customElements.define('himalayan-product-purchase', HimalayanProductPurchase);
if (!customElements.get('himalayan-product')) customElements.define('himalayan-product', HimalayanProduct);
